import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";

// In-app DM Screen: a quick-reference cheat-sheet for D&D 5e rules.
// Pure-static content, no API calls. Searchable across all sections.
const SECTIONS = [
  {
    key: "actions",
    title: "Actions in Combat",
    rows: [
      ["Attack", "Make a melee or ranged attack with a weapon or unarmed strike."],
      ["Cast a Spell", "Cast a spell with a casting time of 1 action."],
      ["Dash", "Gain extra movement equal to your speed for the current turn."],
      ["Disengage", "Your movement doesn't provoke opportunity attacks for the rest of the turn."],
      ["Dodge", "Until your next turn, attacks against you have disadvantage and you make Dex saves with advantage."],
      ["Help", "Either grant advantage to an ally on a check, or grant advantage on the next attack vs. a target."],
      ["Hide", "Make a Stealth check to become hidden."],
      ["Ready", "Prepare an action to trigger on a specific reaction."],
      ["Search", "Make a Wisdom (Perception) or Intelligence (Investigation) check."],
      ["Use an Object", "Interact with a second object beyond the free interaction allowed each turn."],
      ["Two-Weapon Fighting", "Use a bonus action to attack with a light melee weapon in your other hand."],
      ["Grapple / Shove", "Athletics check vs. target's Athletics or Acrobatics."],
    ],
  },
  {
    key: "conditions",
    title: "Conditions",
    rows: [
      ["Blinded", "Can't see, auto-fail sight checks, attacks have disadvantage, attacks vs. you have advantage."],
      ["Charmed", "Can't attack the charmer; charmer has advantage on social checks vs. you."],
      ["Deafened", "Can't hear; auto-fail hearing checks."],
      ["Frightened", "Disadvantage on checks/attacks while source is in line of sight; can't move closer to source."],
      ["Grappled", "Speed becomes 0; ends if grappler is incapacitated or you move out of reach."],
      ["Incapacitated", "Can't take actions or reactions."],
      ["Invisible", "Heavily obscured; attacks have advantage, attacks vs. you have disadvantage."],
      ["Paralyzed", "Incapacitated; can't move/speak; auto-fail Str/Dex saves; attacks have advantage; auto-crits within 5 ft."],
      ["Petrified", "Transformed to stone; incapacitated; resistant to all damage; immune to poison & disease."],
      ["Poisoned", "Disadvantage on attacks and ability checks."],
      ["Prone", "Move only by crawling (½ speed); disadvantage on attacks; melee attacks vs. you have advantage, ranged disadvantage."],
      ["Restrained", "Speed 0; attacks have disadvantage, attacks vs. you have advantage; disadvantage on Dex saves."],
      ["Stunned", "Incapacitated; can't move; auto-fail Str/Dex saves; attacks have advantage."],
      ["Unconscious", "Incapacitated, prone, drops what it holds; auto-fail Str/Dex saves; attacks have advantage; melee within 5 ft auto-crit."],
      ["Exhaustion 1–6", "1: Disadv on checks · 2: Speed halved · 3: Disadv on attacks/saves · 4: HP max halved · 5: Speed 0 · 6: Death."],
    ],
  },
  {
    key: "dcs",
    title: "Typical DCs",
    rows: [
      ["Very Easy", "5"],
      ["Easy", "10"],
      ["Medium", "15"],
      ["Hard", "20"],
      ["Very Hard", "25"],
      ["Nearly Impossible", "30"],
      ["Spell Save DC", "8 + proficiency + spellcasting modifier"],
      ["Group Check", "Half or more of the group must succeed."],
      ["Passive Check", "10 + ability mod + proficiency (if applicable)"],
    ],
  },
  {
    key: "cover",
    title: "Cover & Vision",
    rows: [
      ["Half Cover", "+2 to AC and Dex saves."],
      ["Three-Quarters Cover", "+5 to AC and Dex saves."],
      ["Total Cover", "Cannot be targeted directly by an attack or spell."],
      ["Lightly Obscured", "Disadvantage on Wisdom (Perception) checks relying on sight."],
      ["Heavily Obscured", "Effectively blinded — area is treated as opaque."],
      ["Bright Light", "Normal vision."],
      ["Dim Light", "Lightly obscured."],
      ["Darkness", "Heavily obscured."],
    ],
  },
  {
    key: "rest",
    title: "Rests & Healing",
    rows: [
      ["Short Rest", "1 hour. Spend Hit Dice to heal (1d{HD}+CON each). Some abilities recharge."],
      ["Long Rest", "8 hours. Restore all HP, regain ½ total Hit Dice (rounded down, min 1), reset most abilities."],
      ["Hit Dice Spend", "Roll the die, add CON modifier; one die at a time."],
      ["Death Saves", "DC 10 Constitution. 3 successes stabilize; 3 failures die. Nat 20 = 1 HP, Nat 1 = 2 fails."],
      ["Stabilizing", "DC 10 Wisdom (Medicine) check or Healer's Kit use."],
    ],
  },
  {
    key: "encounter",
    title: "Encounter Difficulty (per character)",
    rows: [
      ["Easy XP threshold", "L1: 25 · L2: 50 · L3: 75 · L4: 125 · L5: 250 · L10: 600 · L15: 1300 · L20: 2800"],
      ["Medium", "L1: 50 · L2: 100 · L3: 150 · L4: 250 · L5: 500 · L10: 1200 · L15: 2800 · L20: 5700"],
      ["Hard", "L1: 75 · L2: 150 · L3: 225 · L4: 375 · L5: 750 · L10: 1900 · L15: 4300 · L20: 8500"],
      ["Deadly", "L1: 100 · L2: 200 · L3: 400 · L4: 500 · L5: 1100 · L10: 2800 · L15: 5900 · L20: 12700"],
      ["Multiple Monsters", "×1.5 (2) · ×2 (3-6) · ×2.5 (7-10) · ×3 (11-14) · ×4 (15+)"],
    ],
  },
  {
    key: "travel",
    title: "Travel & Distances",
    rows: [
      ["Walking pace (slow)", "2 mi/h · 18 mi/day · stealth allowed · −5 passive perception"],
      ["Walking pace (normal)", "3 mi/h · 24 mi/day"],
      ["Walking pace (fast)", "4 mi/h · 30 mi/day · −5 passive perception"],
      ["Forced March", "1 hour past 8: CON save DC 10 + 1 per extra hour or gain exhaustion."],
      ["Vehicle on road", "8 mi/h (cart) · 10 mi/h (carriage) · 16 mi/h (galloping horse)"],
    ],
  },
];

export const DMScreen = ({ open, onOpenChange }) => {
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();

  const matches = (s) => {
    if (!q) return true;
    return s.toLowerCase().includes(q);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-5xl w-[95vw] max-h-[90vh] overflow-hidden flex flex-col bg-card border-border p-0"
        data-testid="dm-screen-dialog"
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-border">
          <DialogTitle className="text-xl font-serif text-foreground">DM Screen — Quick Reference</DialogTitle>
          <p className="text-xs text-muted-foreground mt-1">
            5e essentials at a glance. Search filters every section live.
          </p>
          <div className="relative mt-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search rules — e.g. 'restrained', 'cover', 'long rest', 'DC'…"
              className="pl-9 bg-muted/30 border-border"
              data-testid="dm-screen-search"
              autoFocus
            />
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4" data-testid="dm-screen-body">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {SECTIONS.map((sec) => {
              const filtered = sec.rows.filter(([k, v]) => matches(k) || matches(v));
              if (q && filtered.length === 0) return null;
              return (
                <section
                  key={sec.key}
                  className="bg-muted/20 border border-border rounded-md p-3"
                  data-testid={`dm-screen-section-${sec.key}`}
                >
                  <h3 className="text-[11px] font-bold text-primary uppercase tracking-wider mb-2">{sec.title}</h3>
                  <dl className="space-y-1.5">
                    {(q ? filtered : sec.rows).map(([k, v]) => (
                      <div key={k} className="text-[12px] leading-snug">
                        <dt className="font-semibold text-foreground inline">{k}</dt>
                        <dd className="text-foreground/75 inline"> — {v}</dd>
                      </div>
                    ))}
                  </dl>
                </section>
              );
            })}
            {q && SECTIONS.every((s) => s.rows.filter(([k, v]) => matches(k) || matches(v)).length === 0) && (
              <p className="col-span-full text-sm text-muted-foreground text-center py-8 italic">
                No matches for &ldquo;{query}&rdquo;.
              </p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DMScreen;
